import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import usePrevious from '@deltek/specification-client/core/Utilities/Hooks/usePrevious'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { IconButton, Input, InputAdornment } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import ClearIcon from '@material-ui/icons/Clear'
import SearchIcon from '@material-ui/icons/Search'
import clsx from 'clsx'
import { CategoryPanel } from 'models/category'
import {
    GetProjectElementNodesInput,
    NodeSearchFilter,
} from 'models/getProjectElementNodesInput'
import { MaxElementsPerBatch } from 'pages/ProjectsPage/WorkspacePage'
import React, { useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
    escapeSearchText,
    normalizeSearchText,
    unescapeSearchText,
} from 'shared/utils/stringUtils'

const useShowHideBarStyles = makeStyles((theme: Theme) => ({
    searchIcon: {
        color: '#777777',
    },
    searchFieldWrapper: {
        height: 36,
        width: '100%',
        backgroundColor: theme.palette.common.white,
        borderRadius: 4,
        border: '1px solid #DDDDDD',
        margin: theme.spacing(2, 2.5, 0.25, 2),
    },
    searchFieldText: {
        fontSize: pxToRem(13),
        marginLeft: theme.spacing(0.5),
        marginTop: theme.spacing(0.2),
        padding: theme.spacing(0.875, 0, 0.875),
    },
    elementTypeButton: {
        backgroundColor: theme.palette.common.white,
        borderRadius: pxToRem(100),
        '&:hover': {
            backgroundColor: theme.palette.common.white,
        },
        minWidth: pxToRem(127),
        '@media (max-width: 1200px)': {
            minWidth: 'unset',
        },
    },

    elementTypeItem: {
        paddingLeft: theme.spacing(2.5),
        '@media (max-width: 1024px)': {
            paddingLeft: theme.spacing(1.25),
        },
    },

    checkIcon: {
        color: '#33B55B',
    },
    checkIconDisabled: {
        color: 'rgba(218, 218, 218, 0.5)',
    },

    clearIcon: {
        color: 'rgba(0, 0, 0, 0.54)',
        fontSize: pxToRem(20),
    },
    adornmentButton: {
        paddingRight: theme.spacing(0.625),
        paddingLeft: 0,

        fontSize: pxToRem(16),
        '&:hover': {
            background: 'transparent',
        },
        '& .MuiSvgIcon-root': {
            fontSize: pxToRem(16),
        },
    },
    searchButton: {
        borderRadius: 0,
        height: pxToRem(33),

        '& .MuiSvgIcon-root': {
            fontSize: pxToRem(16),
        },
    },
}))

enum FormattedElementType {
    ASSEMBLY = 'Assembly',
    PRODUCTFAMILY = 'Family',
    PRODUCTTYPE = 'Product Type',
}

export interface SearchBarLeftMostPaneProps {
    /**
     * The currently selected category panel.
     */
    selectedCatergory?: CategoryPanel | null

    /**
     * Function to call API for searching element cards.
     * @param input - The input parameters for searching project element nodes.
     */
    handleSearchElementNodes: (input: GetProjectElementNodesInput) => void

    /**
     * Function to reset search filters to their default state.
     */
    resetSearchFilters: () => void

    /**
     * Called when the user submits a whitespace/dash-only query. The backend
     * rejects empty searches, so the parent must render the
     * "No sections match your search" empty state without firing the query.
     */
    onWhitespaceSearch?: () => void

    /**
     * The search keyword used for filtering element nodes.
     */
    searchKeyword?: string

    /**
     * The filters applied during the search operation.
     */
    searchFilters?: NodeSearchFilter[]

    /**
     * The currently selected element format.
     */
    selectedElementFormat?: string
}

const SearchBarLeftMostPane = ({
    selectedCatergory,
    handleSearchElementNodes,
    resetSearchFilters,
    onWhitespaceSearch,
    searchKeyword,
    searchFilters,
    selectedElementFormat,
}: SearchBarLeftMostPaneProps) => {
    const classes = useShowHideBarStyles()
    const { projectId } = useParams<{ projectId?: string }>()
    const prevCategory = usePrevious(selectedCatergory)

    const toProjectElementType = (el: string): ProjectElementType =>
        el === FormattedElementType.PRODUCTFAMILY
            ? ('PRODUCTFAMILY' as ProjectElementType)
            : (el.replace(/\s/g, '').toUpperCase() as ProjectElementType)

    const getInitialIncludedElements = (filters?: NodeSearchFilter[]) =>
        filters
            ? Object.values(FormattedElementType).map((el) => ({
                  element: el,
                  isIncluded: filters.some(
                      (f) => f.projectElementType === toProjectElementType(el)
                  ),
              }))
            : Object.values(FormattedElementType).map((el) => ({
                  element: el,
                  isIncluded: true,
              }))

    const [includedElements, setIncludedElements] = React.useState(() =>
        getInitialIncludedElements(searchFilters)
    )

    const [popperAnchorEl] = React.useState<HTMLElement | null>(null)
    const searchBarRef = React.useRef<HTMLElement | null>(null)
    const searchInputRef = React.useRef<HTMLInputElement | null>(null)
    const [searchText, setSearchText] = React.useState<string>(
        searchKeyword ? unescapeSearchText(searchKeyword) : ''
    )

    /*commenting this out for version 2
    const handleDisplayAdvancedFilters = () => {
        setPopperAnchorEl(searchBarRef?.current)
    }*/

    const setTextToSearch = useCallback(
        (searchTextValue: string) => {
            setSearchText(searchTextValue)
            if (searchTextValue === '') {
                resetSearchFilters()
            }
        },
        [setSearchText, resetSearchFilters]
    )

    const clearSearch = () => {
        setSearchText('')
        resetSearchFilters()
        searchInputRef.current?.focus()
    }

    const goSearch = useCallback(() => {
        if (searchText === '') return

        const normalizedSearchText = normalizeSearchText(searchText)

        if (normalizedSearchText === '') {
            // Whitespace/dash-only input. Backend rejects empty search text;
            // signal the parent to render the empty-state UI directly.
            onWhitespaceSearch?.()
            return
        }

        const escapedSearchText = escapeSearchText(normalizedSearchText)

        handleSearchElementNodes({
            input: {
                limit: MaxElementsPerBatch,
                categoryId:
                    selectedCatergory?.id === 'all'
                        ? undefined
                        : (selectedCatergory?.id as string),
                skip: 0,
                onlyElementsAssignedToProject: true,
                projectId,
                projectElementType: Object.keys(
                    ProjectElementType
                ) as ProjectElementType[],
                isProductFamilyGroupView:
                    selectedElementFormat === 'DIVISIONS',
                searchFilters: includedElements
                    .filter((e) => e.isIncluded)
                    .map((e) => ({
                        projectElementType: toProjectElementType(e.element),
                        searchText: escapedSearchText,
                    })),
            },
        })
    }, [
        includedElements,
        selectedCatergory,
        searchText,
        handleSearchElementNodes,
        onWhitespaceSearch,
        selectedElementFormat,
        projectId,
    ])

    const handleKeyDown = useCallback(
        (
            event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
            if (event.key === 'Enter') {
                goSearch()
            }
        },
        [goSearch]
    )
    /** Sync local searchText state with searchKeyword prop
     * This ensures the search text is preserved when switching views (Assembly/Division)
     */
    const prevSearchKeyword = usePrevious(searchKeyword)
    React.useEffect(() => {
        if (
            searchKeyword !== undefined &&
            searchKeyword !== prevSearchKeyword
        ) {
            // Unescape backslash-escaped regex operators so the search box
            // doesn't display the backslash that was added for backend safety.
            const unescapedKeyword = unescapeSearchText(searchKeyword)
            // Skip sync when searchKeyword is just the normalized form of the
            // currently typed text — that's the round-trip from our own
            // goSearch dispatch and would clobber the user's typed dashes.
            const currentNormalized = normalizeSearchText(searchText)
            if (currentNormalized !== unescapedKeyword) {
                setSearchText(unescapedKeyword)
            }
        }
    }, [searchKeyword, prevSearchKeyword, searchText])

    /** Reset search text and includedElements when selected category changes
     * Only reset if there's no active search (searchFilters or searchKeyword)
     * to prevent clearing user's search when switching views (Assembly/Division)
     */
    React.useEffect(() => {
        if (
            prevCategory &&
            JSON.stringify(prevCategory) !==
                JSON.stringify(selectedCatergory) &&
            !searchFilters &&
            !searchKeyword
        ) {
            setSearchText('')
            setIncludedElements(getInitialIncludedElements())
        }
    }, [selectedCatergory, prevCategory, searchFilters, searchKeyword])

    return (
        <Input
            disableUnderline
            fullWidth
            placeholder={'Search by title, number, or keyword...'}
            ref={searchBarRef}
            inputRef={searchInputRef}
            inputProps={{ 'aria-label': 'search' }}
            classes={{
                input: classes.searchFieldText,
                root: classes.searchFieldWrapper,
            }}
            value={searchText}
            onChange={(ev) => setTextToSearch(ev.target.value)}
            onKeyDown={handleKeyDown}
            endAdornment={
                <InputAdornment position="end">
                    {searchText !== '' && (
                        <IconButton
                            onClick={clearSearch}
                            aria-label="clear search field"
                            classes={{
                                root: classes.adornmentButton,
                            }}
                            disableRipple
                            data-testid="clearSearch"
                        >
                            <ClearIcon className={classes.clearIcon} />
                        </IconButton>
                    )}
                    <IconButton
                        classes={{
                            root: clsx(
                                classes.adornmentButton,
                                classes.searchButton
                            ),
                        }}
                        onClick={goSearch}
                        disableRipple
                        data-testid="btnSearchField"
                        aria-label="search button"
                        role="button"
                    >
                        <SearchIcon
                            classes={{ root: classes.searchIcon }}
                            className={classes.searchIcon}
                        />
                    </IconButton>
                </InputAdornment>
            }
            data-testid="searchField"
        />
    )
}

export default SearchBarLeftMostPane
